/**
 * factDescriptors — the cross-section descriptor / alias map.
 *
 * Each `FactDescriptor` declares ONE logical fact (a `factId`) and the
 * ORDERED list of candidate SED slots that can supply it, each tagged with a
 * tier + a `SourceRef.kind`. The builder resolves the dot-paths against the
 * SED / provider data, turns the present candidates into `TierSource[]`, and
 * feeds them to the EXISTING `reconcileTierSources` (tier-1 wins) so the same
 * logical fact competes as ONE field instead of living in two un-comparable
 * `<sectionId>.<field>` slots.
 *
 * The `kind` declared HERE — not the blanket tier-1 `'provider'` stamp the
 * generic `sedSectionResolver` applies to every SED field — is what the
 * builder uses to derive `FactStatus`. That is the provenance-laundering
 * guard: an AI-researched non-US utility name is declared `'site-context'`
 * (tier-2) here, so it can never read as `verified`.
 *
 * PURE DATA. No I/O. The dot-paths are resolved by the builder; this file
 * only declares the routing table.
 */

import type { ResolverTier, SourceRef } from '../resolvers/types';
import type { FactStatus } from './canonicalFacts';
import { CONTEXT_ONLY_QUEUE_TEXT } from '../gridQueueEvidence';

/** One candidate slot for a logical fact. */
export interface FactCandidate {
  tier: ResolverTier;
  /** The honesty `kind` of THIS slot (drives FactStatus, not the SED stamp). */
  kind: SourceRef['kind'];
  /**
   * Dot-path into the resolution root the builder assembles
   * (`{ sed, report, providerData, resolved }`). e.g. `'sed.grid.data.utilityName'`,
   * `'resolved.projectIntent.powerRequirementMW'`, `'sed.parcel.data.acres'`,
   * `'report.financialAssumptionsAtGen.capexTotalUsd'`. The builder reads
   * `undefined`/`null` as "slot absent" and skips it. `report.` paths resolve
   * only when the builder is handed a report (`ctx.reports[0]` / the scoring
   * slice) — at prompt-build time there is no report yet, so those candidates
   * simply do not compete.
   */
  path: string;
  /** Stable `SourceRef.ref` recorded on the resolved fact. */
  ref: string;
  /** Human-readable provenance label. */
  label: string;
  /**
   * Slot STRING values that mean "absent" (case-insensitive, trimmed) — e.g.
   * the Regrid parcel registry stamps zoning `'Unknown'` when it has no code;
   * asserting "zoning: Unknown" as a governed fact would be a false datum.
   */
  absentValues?: readonly string[];
  /**
   * Sibling-path guard: the candidate is treated as ABSENT when the value at
   * `absentWhen.path` equals any of `equals` (e.g. `lcoe.blendedLcoePerMwhUsd`
   * is not a usable fact when `lcoe.lcoeBasis === 'unavailable'`).
   */
  absentWhen?: { path: string; equals: readonly unknown[] };
  /**
   * Status override when THIS slot candidate wins (mirrors the field of the
   * same name on Claim/Text candidates). Lets a structured-but-unverified
   * figure — e.g. a user-authored utility-letter indication — resolve tier-1
   * WITHOUT masquerading as a verified datum: the fact resolves, labeled.
   */
  statusWhenWinner?: FactStatus;
}

/**
 * X-SED-6 — one DECLARATIVE claim-derived candidate for a logical fact,
 * resolved from the user's siteContext claims (`sed.siteContext.claims`, or the
 * report's frozen `siteContextSnapshot.claims` when the builder is handed a
 * report). Matching is deliberately CONSERVATIVE: category (or sedField) must
 * match AND the text discriminator must hit — a bare `parcel.acres` claim never
 * resolves `siteArea.buildableAcres` unless its text actually says "buildable".
 * Mis-extracted claims (`state === 'mis-extracted'`) never compete.
 */
export interface ClaimCandidate {
  tier: ResolverTier;
  /** The honesty kind — developer/user claims are `'site-context'`. */
  kind: SourceRef['kind'];
  /** `ExtractedClaim.category` values that may carry this fact. */
  categories: readonly string[];
  /** Optional `ExtractedClaim.sedField` values that also bind (exact match). */
  sedFields?: readonly string[];
  /** Conservative discriminator tested against `claim.claim` + `claim.sourceSpan`. */
  textMatch?: RegExp;
  /** Claims whose text matches this are EXCLUDED (disambiguates siblings). */
  textExclude?: RegExp;
  /**
   * How to lift the value out of the matched claim:
   *  - `'numeric'`       — `claimedValue` when finite, else the first number in
   *                        the claim text (via `valuePattern` when provided).
   *  - `'numeric-range'` — a `low–high` band when the text carries one (value
   *                        renders as the band string; both bounds enter the
   *                        validator's numeric allow-set via `numericValues`),
   *                        else degrades to `'numeric'`.
   *  - `'text'`          — `claimedValue` when it is a non-empty string.
   *  - `'text-pattern'`  — X-SED-7: the FIRST DEFINED capture group of
   *                        `valuePattern` against the claim text. For facts
   *                        whose value is embedded IN the claim prose rather
   *                        than carried by `claimedValue` (e.g. the pipeline
   *                        NAME inside "Tennessee Gas Pipeline (TGP) is
   *                        operated by Kinder Morgan" — `claimedValue` is the
   *                        operator, not the pipeline).
   */
  extract: 'numeric' | 'numeric-range' | 'text' | 'text-pattern';
  /**
   * Anchored number pattern (capture group 1 = the number) for `'numeric'`;
   * the value-capture pattern for `'text-pattern'`.
   *
   * For `'numeric'` this doubles as the fact's UNIT CONTRACT. When the
   * pattern needs more than a bare number to match (`…\s*MW`, `…\s*kV`,
   * `\$…M`), a bare-number `claimedValue` may only win the fact once
   * `buildCanonicalFactStore` has corroborated it — the pattern captures that
   * same number in the claim text, or the claim's span states a compatible
   * unit directly after it (GW→MW, ha→acres convert). An uncorroborated
   * number is refused rather than published in the wrong unit.
   */
  valuePattern?: RegExp;
  /**
   * X-SED-7 — for `'numeric-range'`: when present, the band is parsed from
   * the FIRST MATCH of this pattern (its matched substring) instead of the
   * whole claim text. Anchors a cost band to its `$…M` shape so "phase 2-3
   * upgrade … $4–7M" can never resolve the phase numbers as the range.
   */
  rangePattern?: RegExp;
  /** Stable `SourceRef.ref` recorded on the resolved fact. */
  ref: string;
  /** Human-readable provenance label. */
  label: string;
  /**
   * Status stamped when THIS candidate wins the reconciliation — developer
   * claims are `'labeled-unverifiable'` (a developer statement, not a
   * provider-verified value), overriding the default tier-derived status.
   * The degraded-coverage laundering guard still supersedes.
   */
  statusWhenWinner?: FactStatus;
}

/**
 * X-SED-7 — one DECLARATIVE text-derived candidate for a logical fact,
 * resolved by scanning designated SED TEXT POOLS (the curated developer
 * `siteContext.text` brief, uploaded-document `textContent`). The LAST resort
 * of the ladder (typically tier-3): it exists for doc-established facts the
 * claim extractor missed (the live Redhead "Available power indication from
 * utility: ~50 MW" never became a claim). Matching is CONSERVATIVE — the
 * pattern must anchor on a distinctive keyword family AND capture the value
 * in one pass; a pool with no hit contributes nothing.
 */
export interface TextCandidate {
  tier: ResolverTier;
  /** The honesty kind — doc-derived text is `'site-context'`. */
  kind: SourceRef['kind'];
  /**
   * Ordered dot-paths to the text pools. A path may resolve to a STRING
   * (scanned directly) or an ARRAY whose items are strings or objects with a
   * `textContent` / `text` string field (uploaded-document shape). First
   * pool with a hit wins.
   */
  paths: readonly string[];
  /** Keyword-anchored pattern; the FIRST DEFINED capture group = the raw value. */
  pattern: RegExp;
  /** `'numeric'` parses the capture (comma-stripped); `'text'` trims it. */
  extract: 'numeric' | 'text';
  /** Engineering-plausibility band for `'numeric'` (out-of-band ⇒ no hit). */
  min?: number;
  max?: number;
  /** Stable `SourceRef.ref` recorded on the resolved fact. */
  ref: string;
  /** Human-readable provenance label. */
  label: string;
  /** Status stamped when THIS candidate wins (doc text ⇒ `'labeled-unverifiable'`). */
  statusWhenWinner?: FactStatus;
}

/** One logical fact + its ordered candidate slots. */
export interface FactDescriptor {
  factId: string;
  label: string;
  /** Display unit when numeric. */
  unit?: string;
  /** Alternate-basis sibling label (power triplet, cooling axes). */
  basis?: string;
  /** Whether this fact is a named-entity (string) vs a numeric/categorical value. */
  valueKind: 'numeric' | 'entity' | 'categorical';
  /** Ordered candidate slots; the builder keeps every PRESENT one as a TierSource. */
  candidates: FactCandidate[];
  /** X-SED-6 — claim-derived candidates (siteContext claims), matched conservatively. */
  claimCandidates?: readonly ClaimCandidate[];
  /** X-SED-7 — text-derived candidates (siteContext.text / uploaded docs), last resort. */
  textCandidates?: readonly TextCandidate[];
}

/**
 * The descriptor table. Each entry collapses a set of section-scoped SED
 * slots (and resolver outputs) into one reconciled `factId`.
 *
 * `resolved.<registryId>.<fieldKey>` paths read the output of the existing
 * `buildAllRegistryResolvers` manifest (already tier-laddered for power /
 * cooling / COD / financial assumptions). `sed.<section>.data.<field>` paths
 * read the raw SED slot. `providerData.<...>` is reserved for live provider
 * aggregate reads when the builder is handed one.
 */
export const FACT_DESCRIPTORS: readonly FactDescriptor[] = [
  // ── Electric utility name (grid provider ⊕ siteResearch AI) ──
  {
    factId: 'electricUtility.name',
    label: 'Electric utility',
    valueKind: 'entity',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.grid.data.utilityName',
        ref: 'sed:grid.utilityName',
        label: 'Grid provider (HIFLD)',
      },
      {
        // AI site-research is NOT a verified provider — tier-2 site-context.
        tier: 2,
        kind: 'site-context',
        path: 'sed.siteResearch.data.localInfrastructure.utilityProvider',
        ref: 'sed:siteResearch.localInfrastructure.utilityProvider',
        label: 'Site research (AI)',
      },
    ],
  },

  // ── X-SED-7: doc-tier utility taxonomy (retail vs G&T) ──
  //
  // The live Redhead reviewer flagged "1803 Electric Cooperative" as a wrong
  // utility because the single `electricUtility.name` slot carried the AI
  // guess while the developer's docs establish BOTH: BECI is the RETAIL
  // provider and 1803 EC the G&T — two correct names at different tiers of
  // the same service. Resolving each as its OWN labeled fact (never
  // competing inside `electricUtility.name`) kills that collapse. Values are
  // developer statements ⇒ `labeled-unverifiable`; `valueKind` is
  // deliberately 'categorical' so multiple same-prefix name facts NEVER
  // enter the entity gate's per-category sibling-swap check (naming the
  // retail provider without the G&T is legitimate prose) — the NER still
  // grounds the names via the fact-value corpus + contextEntities.
  {
    factId: 'grid.utilityRetailName',
    label: 'Utility (retail, developer docs)',
    valueKind: 'categorical',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        textMatch: /\bretail\b/i,
        extract: 'text',
        ref: 'claim:grid.utilityRetailName',
        label: 'Developer docs — retail electric provider',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },
  {
    factId: 'grid.utilityGtName',
    label: 'Utility (G&T, developer docs)',
    valueKind: 'categorical',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        textMatch: /\bG\s*&\s*T\b|generation\s+(?:and|&)\s+transmission/i,
        extract: 'text',
        ref: 'claim:grid.utilityGtName',
        label: 'Developer docs — G&T provider',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── Power triplet (declared / IT-load / facility / MVA / PUE) ──
  {
    factId: 'power.declaredMw',
    label: 'Power (declared)',
    unit: 'MW',
    basis: 'declared',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'resolved.projectIntent.powerRequirementMW',
        ref: 'resolved:projectIntent.powerRequirementMW',
        label: 'User project intent',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.power.data.declaredMw',
        ref: 'sed:power.declaredMw',
        label: 'Power semantics',
      },
    ],
  },
  {
    factId: 'power.itLoadMw',
    label: 'IT load',
    unit: 'MW',
    basis: 'it-load',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.power.data.itLoadMw',
        ref: 'sed:power.itLoadMw',
        label: 'Power semantics (resolvePowerStack)',
      },
    ],
  },
  {
    factId: 'power.facilityMw',
    label: 'Facility power',
    unit: 'MW',
    basis: 'facility',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.power.data.facilityMw',
        ref: 'sed:power.facilityMw',
        label: 'Power semantics (resolvePowerStack)',
      },
    ],
  },
  {
    factId: 'power.apparentMva',
    label: 'Apparent power',
    unit: 'MVA',
    basis: 'apparent',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.power.data.apparentMva',
        ref: 'sed:power.apparentMva',
        label: 'Power semantics (resolvePowerStack)',
      },
    ],
  },
  {
    factId: 'power.pueValue',
    label: 'Datacenter power basis (PUE)',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.cooling.data.resolvedPue',
        ref: 'sed:cooling.resolvedPue',
        label: 'Cooling section resolved PUE',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.power.data.pueValue',
        ref: 'sed:power.pueValue',
        label: 'Power semantics PUE',
      },
    ],
  },

  // ── Firm capacity / ELCC accreditation ──
  {
    factId: 'firmCapacity.accreditationTarget',
    label: 'Firm capacity / ELCC accreditation target',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'sed.firmCapacity.data.accreditationTarget',
        ref: 'sed:firmCapacity.accreditationTarget',
        label: 'Capacity-credit intent',
      },
    ],
  },
  {
    factId: 'firmCapacity.targetMw',
    label: 'Firm capacity target',
    unit: 'MW',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'sed.firmCapacity.data.targetMw',
        ref: 'sed:firmCapacity.targetMw',
        label: 'Capacity-credit intent',
      },
      // 2026-08-13 (Redhead RCA): the report-setup wizard carries an AUTHORED
      // "Firm-capacity basis (MW)" (typedPreferences.firmCapacityBasisMW =
      // 300) that never reached this fact — the store said ABSENT and the
      // grounding gate blocked the report's own configured value as
      // "invented". The selected type's prefs are exposed on the resolution
      // root as `prefs`; the SED slot above still wins when both exist.
      // ref deliberately 'setup:' — same-tier winners tie-break on ref
      // localeCompare (resolveCore), and 'sed:' < 'setup:' keeps the SED
      // capacity-credit slot authoritative when both exist.
      {
        tier: 1,
        kind: 'report-override',
        path: 'prefs.firmCapacityBasisMW',
        ref: 'setup:firmCapacityBasisMW',
        label: 'Report setup — firm-capacity basis',
      },
    ],
  },
  {
    factId: 'firmCapacity.accreditationNote',
    label: 'Firm capacity note',
    valueKind: 'entity',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'sed.firmCapacity.data.accreditationNote',
        ref: 'sed:firmCapacity.accreditationNote',
        label: 'Capacity-credit intent',
      },
    ],
  },

  // ── ISO / RTO region (grid provider ⊕ firmCapacity mirror) ──
  {
    factId: 'isoRegion',
    label: 'ISO/RTO region',
    valueKind: 'entity',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.grid.data.isoRegion',
        ref: 'sed:grid.isoRegion',
        label: 'Grid provider',
      },
      {
        tier: 2,
        kind: 'manual',
        path: 'sed.firmCapacity.data.isoRegion',
        ref: 'sed:firmCapacity.isoRegion',
        label: 'Firm-capacity (mirrors grid)',
      },
    ],
  },

  // ── Cooling axes (strategy / generation / heat-rejection) ──
  {
    factId: 'cooling.strategy',
    label: 'Cooling strategy (rack)',
    basis: 'rack',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'resolved.projectIntent.coolingStrategy',
        ref: 'resolved:projectIntent.coolingStrategy',
        label: 'User cooling choice',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.cooling.data.coolingStrategy',
        ref: 'sed:cooling.coolingStrategy',
        label: 'Cooling section',
      },
    ],
  },
  {
    factId: 'cooling.generation',
    label: 'Cooling generation (BTM machine)',
    basis: 'generation',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.cooling.data.coolingGeneration',
        ref: 'sed:cooling.coolingGeneration',
        label: 'Cooling section',
      },
      {
        tier: 2,
        kind: 'manual',
        path: 'sed.projectIntent.coolingGeneration',
        ref: 'sed:projectIntent.coolingGeneration',
        label: 'Project intent',
      },
    ],
  },
  {
    factId: 'cooling.bopHeatRejection',
    label: 'Cooling heat-rejection sink',
    basis: 'heat-rejection',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.cooling.data.bopHeatRejection',
        ref: 'sed:cooling.bopHeatRejection',
        label: 'Cooling section',
      },
      {
        tier: 2,
        kind: 'manual',
        path: 'sed.projectIntent.bopHeatRejection',
        ref: 'sed:projectIntent.bopHeatRejection',
        label: 'Project intent',
      },
    ],
  },

  // ── Site area (selected study geometry, then legacy profile/parcel) ──
  // A valid frozen studyAreaScope selects its own geometry-bound candidate in
  // resolveDescriptor; whole-property aliases are a different scope.
  //
  // TIERS ARE LOAD-BEARING: they MUST mirror the deterministic datasheet builder
  // `reconstructLandFromSed` (landReconstruction.ts:93), which resolves
  // `datasets.land.acres` as `siteProfile.acres ?? flood.acres ?? parcel.acres`
  // — the user-drawn boundary polygon (`siteProfile`) is authoritative over the
  // Regrid legal parcel. The reconciler tie-breaks EQUAL tiers alphabetically by
  // `ref` (resolveCore.ts:45), so two tier-1 slots let `sed:parcel.acres` win over
  // `sed:siteProfile.acres` (p < s) — the OPPOSITE of the datasheet, so the
  // governed narrative value diverged from the rendered acreage (the live Redhead
  // defect: datasheet 1170.1 ac vs a different parcel figure). Making the parcel
  // tier-2 lets `siteProfile` win by TIER, keeping the ONE governed value identical
  // to the datasheet while the disagreeing parcel is still surfaced as a conflict.
  {
    factId: 'siteArea.acres',
    label: 'Site area',
    unit: 'acres',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'sed.studyAreaScope.grossAreaAcres',
        ref: 'sed:studyAreaScope.grossAreaAcres',
        label: 'Selected study geometry gross area; not usable area',
      },
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.siteProfile.data.acres',
        ref: 'sed:siteProfile.acres',
        label: 'User-drawn boundary (site-profile)',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.parcel.data.acres',
        ref: 'sed:parcel.acres',
        label: 'Parcel registry (Regrid)',
      },
    ],
  },

  // ── Site area as the DATASHEET renders it (user-drawn boundary) ──
  //
  // `siteArea.acres` above is the governed site area, and a USER AMENDMENT can
  // legitimately override it — including the "Auto-synced from site context"
  // amendments the platform writes itself. Live Redhead: an auto-sync carrying
  // `amendment:parcel.data.acres` = 800 outranked the tier-1 user-drawn boundary
  // of 1170.1, so `siteArea.acres` resolved to 800 while the datasheet, the map
  // and every narrative continued to render 1170.1 (per `reconstructLandFromSed`,
  // which reads siteProfile FIRST and never consults amendments).
  //
  // The narrative figure was therefore correct and ungovernable: with no fact
  // holding 1170, "1170 acres" fell through to `siteArea.underControlAcres`
  // (700–1,100) and blocked as a value-mismatch demanding 1100 — a FALSE
  // ACCUSATION against a figure the product itself displays.
  //
  // This fact governs that rendered quantity directly. It is deliberately
  // ADDITIVE: it changes no precedence, no scoring input (absent from
  // SCORING_FACT_IDS by design) and no existing fact's value. It is also NOT in
  // US_PROVIDER_DEPENDENT_FACT_IDS — a user-drawn boundary is authoritative
  // regardless of coverage, exactly as intent-sourced facts are.
  //
  // NOTE the residual this does NOT fix: `siteArea.acres` still disagrees with
  // the datasheet whenever such an amendment exists. That is an amendment-
  // precedence question with blast radius well beyond grounding, so it is
  // surfaced rather than silently changed here.
  {
    factId: 'siteArea.boundaryAcres',
    label: 'Site area (user-drawn boundary)',
    unit: 'acres',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'sed.studyAreaScope.grossAreaAcres',
        ref: 'sed:studyAreaScope.grossAreaAcres',
        label: 'Selected study geometry gross area; not usable area',
      },
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.siteProfile.data.acres',
        ref: 'sed:siteProfile.acres',
        label: 'User-drawn boundary (site-profile)',
      },
    ],
  },

  // ── Wetlands (USFWS NWI acreage) ──
  //
  // Governs the wetland acreage the narrative may assert. The SED slot
  // `environmental.data.wetlandAcres` is the single source both the datasheet
  // (`datasets.environmental.wetlandAcreage`, mapped 1:1 in sedDatasets) and the
  // risk/site-details/extended-data builders read, canonicalised to one 1-decimal
  // value at the provider (usfwsHabitat) so "66 / 65.6 / 65.7" can no longer
  // diverge. USFWS NWI is a US-only provider ⇒ member of the laundering set below.
  //
  // C2 P2-12 — the prompt line must say the value is a whole-feature total:
  // NWI sums each intersecting feature's FULL source acreage, not the area
  // inside the parcel (usfwsHabitat `wetlandAcreageBasis: 'source-feature-total'`).
  // The qualifier rides on `basis` (rendered only in the CANONICAL FACTS prompt
  // line), NOT in `label`: the grounding validator derives absent-fact
  // attribution keywords from `label`, and adding "source"/"feature"/"parcel"/
  // "acreage" there would let any unmatched parcel acreage be blamed on an
  // ABSENT wetlands fact (non-US sites, NWI outages). The label keeps the same
  // ≥4-letter keyword set (mapped / wetlands / inventory).
  {
    factId: 'wetlands.acres',
    label: 'Mapped NWI wetlands (inventory)',
    unit: 'acres',
    basis: 'full source-feature total, not parcel acreage',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.environmental.data.wetlandAcres',
        ref: 'sed:environmental.wetlandAcres',
        label: 'USFWS NWI wetlands',
      },
    ],
  },

  // ── Target COD (user intent date) ──
  {
    factId: 'cod.targetDate',
    label: 'Target COD',
    valueKind: 'entity',
    candidates: [
      {
        tier: 1,
        kind: 'manual',
        path: 'resolved.projectIntent.inServiceDate',
        ref: 'resolved:projectIntent.inServiceDate',
        label: 'User COD target',
      },
      {
        tier: 2,
        kind: 'manual',
        path: 'sed.projectIntent.inServiceDate',
        ref: 'sed:projectIntent.inServiceDate',
        label: 'SED project-intent COD',
      },
    ],
  },

  // ── Nearest substation distance (on-site KMZ ground truth ⊕ HIFLD) ──
  {
    factId: 'substationDistKm',
    label: 'Nearest substation',
    unit: 'km',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.grid.data.substationDistKm',
        ref: 'sed:grid.substationDistKm',
        label: 'Grid provider / on-site KMZ',
      },
    ],
  },

  // ── X-SED-7: substation taxonomy — HIFLD-nearest vs doc-designated POI ──
  //
  // The live Redhead reviewer collapsed TWO distinct facts into one slot
  // (the acreage-taxonomy pattern): GILLIS is the HIFLD-NEAREST substation
  // (5.2 km, a registry proximity fact) while the developer's docs designate
  // the 1803 Moss Bluff 230 kV substation as the INTERCONNECTION POINT (a
  // commercial/engineering designation). Both are correct; rendering both
  // with distinct labels stops the reviewer flagging either as "wrong".
  // 'categorical' valueKind — see the utility-taxonomy note above.
  {
    factId: 'grid.substationName',
    label: 'Nearest substation (HIFLD)',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.grid.data.substationName',
        ref: 'sed:grid.substationName',
        label: 'Grid provider (HIFLD)',
      },
    ],
  },
  {
    factId: 'grid.interconnectionSubstationName',
    label: 'Interconnection substation (developer-designated)',
    valueKind: 'categorical',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        textMatch:
          /anchor\s+(?:facility|substation)|interconnection\s+(?:point|substation|facility)|point\s+of\s+interconnection|\bPOI\b/i,
        extract: 'text',
        ref: 'claim:grid.interconnectionSubstationName',
        label: 'Developer docs — designated interconnection point',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── Other governed distances (grounding FIX 1 — the km allow-set) ──
  //
  // Mirrors the X-SED-6 acres pattern: the validator's same-unit candidate set
  // must SPAN every governed distance, or a correct prose "204.9 km to the
  // nearest IX" is compared against substationDistKm alone and false-blocks
  // (the live Redhead defect: 10 km-mismatches on a correct report). Each is a
  // real SED slot the narrative routinely quotes.
  {
    factId: 'ix.distanceKm',
    label: 'Nearest IX (geographic proximity)',
    unit: 'km',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.connectivity.data.nearestIxDistKm',
        ref: 'sed:connectivity.nearestIxDistKm',
        label: 'PeeringDB nearest IX',
      },
    ],
  },
  {
    factId: 'reactor.distanceKm',
    label: 'Nearest reactor',
    unit: 'km',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.nuclearInfra.data.nearestReactorDistKm',
        ref: 'sed:nuclearInfra.nearestReactorDistKm',
        label: 'NRC reactor registry',
      },
    ],
  },
  {
    factId: 'pipeline.distanceKm',
    label: 'Nearest gas pipeline',
    unit: 'km',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.fuelSupply.data.nearestPipelineKm',
        ref: 'sed:fuelSupply.nearestPipelineKm',
        label: 'EIA pipeline registry',
      },
      /*
       * User KMZ/CAD corridor slot (2026-08-17) — the SAME curated value the
       * generation prompt renders as "Nearest gas pipeline corridor: X.XXkm"
       * (siteEvidence.ts ~3704). Live Redhead: the EIA slot is null while this
       * slot holds the user's own corridor measurement; ungoverned, the
       * prompt's own line blocked as invented. Sourcing the fact from the SAME
       * slot the prompt renders is the two-halves-must-agree rule — a raw
       * re-derivation from corridors was reviewed and REJECTED because it
       * could contradict the platform's rendered value. Equal tier: resolveCore
       * tie-breaks alphabetically by ref, 'sed:f…' < 'sed:o…', so EIA wins
       * whenever both are present.
       */
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.onSiteInfrastructure.data.nearestPipelineKm',
        ref: 'sed:onSiteInfrastructure.nearestPipelineKm',
        label: 'User KMZ pipeline corridor',
      },
    ],
  },

  // ── Technology module rating (grounding FIX 1 — the MW allow-set) ──
  //
  // The typical per-module electrical output of the selected technology class
  // (e.g. a 77 MWe SMR module). Tier-2 class-typical data, never a site
  // measurement ⇒ resolves 'estimated', so prose quoting it must qualify it.
  {
    factId: 'technology.typicalModuleMWe',
    label: 'Technology module output (typical)',
    unit: 'MW',
    basis: 'per-module, class-typical',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.technology.data.typicalModuleMWe',
        ref: 'sed:technology.typicalModuleMWe',
        label: 'Technology class profile',
      },
    ],
  },

  // Saved wind-resource CF is a fractional provider/resource estimate. It
  // never supplies the separate project financial capacityFactorPct fact.
  // The builder converts the native 0–1 fraction to percentage points.
  {
    factId: 'wind.resourceCapacityFactorPct',
    label: 'Estimated wind resource capacity factor',
    unit: '%',
    basis: 'wind resource estimate, not project/financial utilization',
    valueKind: 'numeric',
    candidates: [],
  },

  // ── Economics anchors (financial-assumptions resolver) ──
  {
    factId: 'capacityFactorPct',
    // FIN-1 / 3.8 — unit added. The fact was previously UNIT-LESS, so the
    // grounding validator's numeric index carried it with `unit: undefined`
    // and `checkNumerics` skipped it outright: a prose capacity factor was
    // ungoverned. It now joins the `%` class, whose matcher is SUBJECT-KEYED
    // (see `PERCENT_SUBJECTS` in groundingValidator) precisely so a capacity
    // factor can never reconcile against an IRR that happens to share `%`.
    unit: '%',
    label: 'Capacity factor',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'site-canonical',
        path: 'resolved.financialAssumptions.capacityFactorPct',
        ref: 'resolved:financialAssumptions.capacityFactorPct',
        label: 'Financial assumptions',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.lcoe.data.capacityFactorPct',
        ref: 'sed:lcoe.capacityFactorPct',
        label: 'LCOE section',
      },
    ],
  },
  {
    factId: 'fuelCostPerMwhUsd',
    label: 'LCOE fuel cost',
    unit: 'USD/MWh',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.fuelSupply.data.fuelCostPerMwhUsd',
        ref: 'sed:fuelSupply.fuelCostPerMwhUsd',
        label: 'Fuel-supply provider',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.lcoe.data.fuelCostPerMwhUsd',
        ref: 'sed:lcoe.fuelCostPerMwhUsd',
        label: 'LCOE section',
      },
    ],
  },
  {
    factId: 'ppaPricePerMwhUsd',
    label: 'PPA price',
    unit: 'USD/MWh',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'site-canonical',
        path: 'resolved.financialAssumptions.ppaPricePerMwhUsd',
        ref: 'resolved:financialAssumptions.ppaPricePerMwhUsd',
        label: 'Financial assumptions',
      },
    ],
  },
  {
    factId: 'capexPerKwUsd',
    label: 'CAPEX per kW',
    unit: 'USD/kW',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'site-canonical',
        path: 'resolved.financialAssumptions.capexPerKwUsd',
        ref: 'resolved:financialAssumptions.capexPerKwUsd',
        label: 'Financial assumptions',
      },
    ],
  },
  /*
   * `discountRatePct` — RETIRED (FIN-1 / audit 3.8, No-Dormant-Code).
   *
   * The descriptor read `resolved.financialAssumptions.discountRatePct`, and
   * `discountRatePct` IS NOT A FIELD ON `FinancialAssumptionsCore`. The FA
   * resolver lists the key in `FA_FIELD_KEYS` but every lookup
   * (`siteAssumptions?.['discountRatePct']`) is `undefined`, so the resolver
   * publishes nothing and the fact resolved ABSENT on every site ever built —
   * a governed "Discount rate: NOT ESTABLISHED" line in every canonical prompt
   * block for a quantity the model DOES hold, under a different name.
   *
   * The real, populated discount rates are the financial stamp's `waccPct`
   * (the engine's actual discount rate) and `hurdleRatePct` (the rate the
   * verdict bands IRR against) — both governed below. Repointing rather than
   * deleting would have kept a third name for the same two numbers.
   */

  /* ── FIN-1 — the DETERMINISTIC FINANCIAL STAMP (`report.financialAnalysis`) ──
   *
   * The Financial Viability report is the one type whose figures are computed
   * and PERSISTED before any narrative is written: `buildFinancialAnalysis`
   * runs `projectFinance.recompute` and stamps `report.financialAnalysis`
   * (headline + band + score). Nothing here re-derives that math — these
   * descriptors READ the snapshot, which makes every LCOE / NPV / IRR /
   * payback figure in the prose checkable against the engine that produced the
   * verdict. Before this block the canonical fact store held NO financial
   * figure at all, so an LLM-stated LCOE contradicting the stamp passed the
   * grounding gate silently (audit finding D10).
   *
   * TIER + KIND. Tier-1 (the report's own stamp is the authority for the
   * report's own numbers) with `kind: 'report'`, which is deliberately NOT in
   * `VERIFIED_TIER1_KINDS` — so `deriveStatus` grades these `'estimated'`, the
   * same grade `buildDerivedFacts` gives every computed value ("a model proxy,
   * never a measurement"). They win the ladder; they do not masquerade as
   * measured facts.
   *
   * SCALE. `npvUsd` is PLAIN USD (142000000, not 142). The `USD` unit class in
   * the grounding validator and the `npv_usd` reconciler key both normalise
   * prose scale suffixes ($0.142B ≡ $142M ≡ 142000000) BEFORE comparing, so a
   * correct "$142M" matches and a mis-scaled "$142" does not.
   *
   * `report.` paths resolve only when the builder is handed a report; at
   * prompt-build time there is none and every candidate here simply skips.
   */
  {
    factId: 'finance.lcoeUsdPerMwh',
    label: 'LCOE (financial model)',
    unit: 'USD/MWh',
    basis: 'project-finance engine, nominal — not the pathway-benchmark LCOE',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.lcoeUsdPerMwh',
        ref: 'report:financialAnalysis.headline.lcoeUsdPerMwh',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.npvUsd',
    label: 'Project NPV',
    // PLAIN USD, not $M — see the SCALE note above.
    unit: 'USD',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.npvUsd',
        ref: 'report:financialAnalysis.headline.npvUsd',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.irrPct',
    label: 'Project IRR',
    unit: '%',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.irrPct',
        ref: 'report:financialAnalysis.headline.irrPct',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.hurdleRatePct',
    label: 'IRR hurdle rate',
    unit: '%',
    basis: 'the rate the verdict bands IRR against (explicit override → WACC)',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.hurdleRatePct',
        ref: 'report:financialAnalysis.headline.hurdleRatePct',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.waccPct',
    label: 'WACC',
    unit: '%',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.waccPct',
        ref: 'report:financialAnalysis.headline.waccPct',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.paybackYears',
    label: 'Payback period',
    unit: 'years',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.paybackYears',
        ref: 'report:financialAnalysis.headline.paybackYears',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    // Governed alongside payback because the engine's own banding prose states
    // BOTH in one breath ("Payback (18.4 yr) exceeds the 25 yr design life").
    // With the life ungoverned, that second figure had no candidate to match.
    factId: 'finance.projectLifeYears',
    label: 'Project design life',
    unit: 'years',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.projectLifeYears',
        ref: 'report:financialAnalysis.headline.projectLifeYears',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    // DISTINCT from `capexPerKwUsd` (the live site FA slot) on purpose: this is
    // the AT-GENERATION basis the report's own NPV/LCOE were computed from, and
    // the two legitimately drift once the user edits the model. Folding them
    // into one fact would either hide the drift or manufacture a permanent
    // tier-1-vs-tier-1 conflict line in every prompt.
    factId: 'finance.capexPerKwUsd',
    label: 'CAPEX per kW (financial model)',
    unit: 'USD/kW',
    basis: 'the CAPEX intensity the stamped verdict was computed on',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.capexPerKwUsd',
        ref: 'report:financialAnalysis.headline.capexPerKwUsd',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    factId: 'finance.annualMwh',
    label: 'Annual generation',
    unit: 'MWh',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.headline.annualMwh',
        ref: 'report:financialAnalysis.headline.annualMwh',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    /*
     * The BANDED VERDICT. `categorical`, so it never enters `entityIndex()` —
     * 'green' / 'amber' / 'red' are common words and would false-positive the
     * entity gate's substring matching. It governs the prompt block only: the
     * narrative may not assert a verdict the engine did not band.
     */
    factId: 'finance.verdictBand',
    label: 'Financial verdict band',
    basis: 'green = Viable, amber = Conditional, red = Unfinanceable; ABSENT = verdict withheld',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.band',
        ref: 'report:financialAnalysis.band',
        label: 'Financial model (engine stamp)',
      },
    ],
  },
  {
    /*
     * 0-100 deterministic viability score (mirrors `report.overallScore`).
     * DIMENSIONLESS on purpose: a bare "72" in prose carries no co-located
     * unit, so `extractTaggedNumbers` never tags it and the validator cannot
     * cross-match it against a percentage. It is governed for the PROMPT
     * (the narrative must not invent a different score), not for the matcher.
     */
    factId: 'finance.viabilityScore',
    label: 'Financial viability score (0-100)',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report',
        path: 'report.financialAnalysis.viabilityScore',
        ref: 'report:financialAnalysis.viabilityScore',
        label: 'Financial model (engine stamp)',
      },
    ],
  },

  /* ── SCORING-CRITICAL FACTS (L-tier pillar-scoring rebuild) ─────────────
   *
   * Typed canonical facts the pillar scorer consumes FIRST (prose-regex
   * demoted to a labeled fallback). Each resolves by tier from SED slots →
   * siteContext claims → report-stamped values. They also flow into the
   * canonical prompt block + grounding validator like every other fact.
   */

  // ── Parcel zoning (Regrid registry ⊕ user zoning claims) ──
  {
    factId: 'parcel.zoning',
    label: 'Zoning',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.parcel.data.zoning',
        ref: 'sed:parcel.zoning',
        label: 'Parcel registry (Regrid)',
        // Regrid stamps 'Unknown' when it has no zoning code — that is an
        // absence, not a governed value.
        absentValues: ['unknown', 'n/a', 'none'],
      },
    ],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['regulatory.zoning'],
        sedFields: ['parcel.data.zoning', 'parcel.zoning', 'regulatory.zoning'],
        extract: 'text',
        ref: 'claim:parcel.zoning',
        label: 'Site-context zoning claim',
      },
    ],
  },

  // ── Critical habitat flag (USFWS) ──
  {
    factId: 'env.hasCriticalHabitat',
    label: 'Critical habitat (USFWS)',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.environmental.data.hasCriticalHabitat',
        ref: 'sed:environmental.hasCriticalHabitat',
        label: 'USFWS critical habitat',
      },
    ],
  },

  // ── RD-1 (D) — refdata-only structured screens (ADDITIVE; existing paths
  // above are untouched) ──
  {
    factId: 'env.tribalLandsOnParcel',
    label: 'Tribal land overlap (Census AIANNH)',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.environmental.data.tribalLands.onParcel',
        ref: 'sed:environmental.tribalLands.onParcel',
        label: 'Census AIANNH overlap',
      },
    ],
  },
  {
    factId: 'env.protectedAreasNearestMi',
    label: 'Nearest protected area (PAD-US)',
    valueKind: 'numeric',
    unit: 'mi',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.environmental.data.protectedAreas.nearestDistanceMi',
        ref: 'sed:environmental.protectedAreas.nearestDistanceMi',
        label: 'PAD-US nearest protected area',
      },
    ],
  },
  {
    factId: 'env.regulatedFacilitiesWithin3Mi',
    label: 'Regulated facilities within 3 mi (EPA FRS)',
    valueKind: 'numeric',
    unit: 'count',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.environmental.data.regulatedFacilities.within3MiCount',
        ref: 'sed:environmental.regulatedFacilities.within3MiCount',
        label: 'EPA FRS facility count (3 mi)',
      },
    ],
  },
  {
    factId: 'water.huc12',
    label: 'Watershed (HUC12)',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.waterResource.data.huc12.code',
        ref: 'sed:waterResource.huc12',
        label: 'USGS WBD HUC12',
      },
    ],
  },

  // ── FEMA flood zone + risk band ──
  {
    factId: 'flood.zone',
    label: 'FEMA flood zone',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.flood.data.floodZone',
        ref: 'sed:flood.floodZone',
        label: 'FEMA NFHL',
      },
    ],
  },
  {
    factId: 'flood.risk',
    label: 'FEMA flood risk',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.flood.data.floodRisk',
        ref: 'sed:flood.floodRisk',
        label: 'FEMA NFHL',
      },
    ],
  },

  // ── Grid headroom (ATC / hosting capacity; honest-null today) ──
  {
    factId: 'grid.headroomMw',
    label: 'Grid headroom',
    unit: 'MW',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.transmissionHeadroom.data.atcMw',
        ref: 'sed:transmissionHeadroom.atcMw',
        label: 'Available Transfer Capability (OASIS)',
      },
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.transmissionHeadroom.data.hostingCapacityMw',
        ref: 'sed:transmissionHeadroom.hostingCapacityMw',
        label: 'Distribution hosting capacity',
      },
    ],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        // Bare "ATC" is not enough to establish this site's headroom. Portfolio
        // workbooks commonly carry off-take ATC rows for sibling projects; the
        // live Redhead data-centre SED ingested the "Redhead Solar" row
        // ("offtake ATC planning capacity is 194.3") and promoted it as this
        // project's canonical headroom. Require the claim to say what the
        // number MEANS (headroom / hosting capacity / available transfer
        // capacity). A real "ATC headroom" claim still matches on headroom.
        textMatch: /headroom|hosting capacity|available (?:transfer )?capacity/i,
        extract: 'numeric',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*MW/i,
        ref: 'claim:grid.headroomMw',
        label: 'Site-context grid-headroom claim',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── X-SED-7: utility-indicated available capacity (doc-tier) ──
  //
  // DISTINCT from `grid.headroomMw` (study-verified ATC / hosting capacity,
  // honest-null today): the utility's own WRITTEN INDICATION in the
  // developer's uploaded docs ("Available power indication from utility:
  // ~50 MW … treat as indication, not commitment"). The live Redhead
  // reviewer read the honest-null headroom as "50 MW is invented" when the
  // 50 was the utility's own figure — presenting BOTH lines (headroom NOT
  // ESTABLISHED + the labeled indication) kills that false-fabrication
  // class. Resolution: siteContext claims first (tier-2), then a
  // conservative keyword-anchored scan of the developer text pools (tier-3,
  // for the live case where the claim extractor missed it). Always
  // `labeled-unverifiable` — an indication is not a commitment. The fact
  // label is deliberately LEAN ("Grid capacity indication") so the
  // absent-fact keyword tripwire gains only the distinctive
  // indication/indicated tokens, never common words like "available".
  {
    factId: 'grid.headroomIndicatedMw',
    label: 'Grid capacity indication',
    unit: 'MW',
    basis: 'utility indication, not a commitment',
    valueKind: 'numeric',
    candidates: [
      {
        // Structured utility-letter figure (grid.capacityDetail) — the
        // authored-schema transform now preserves the MW it used to destroy.
        // Tier 1 BUT labeled-unverifiable: a letter indication resolves
        // first, without ever masquerading as verified ATC (grid.headroomMw
        // keeps its honest-null atcMw slot untouched). The basis gate keeps
        // the door open for a future study/executed slot on grid.headroomMw
        // without ambiguity.
        tier: 1,
        kind: 'site-context',
        path: 'sed.grid.data.capacityDetail.mw',
        absentWhen: { path: 'sed.grid.data.capacityDetail.basis', equals: ['study', 'executed'] },
        ref: 'sed:grid.capacityDetail.mw',
        label: 'Utility letter — stated available capacity (indication)',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        textMatch:
          /indicat\w*[^.\n]{0,50}?(?:available|capacit|power|utility)|(?:available|utility)[^.\n]{0,50}?indicat/i,
        extract: 'numeric',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*MW/i,
        ref: 'claim:grid.headroomIndicatedMw',
        label: 'Developer docs — utility-indicated available capacity',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
    textCandidates: [
      {
        tier: 3,
        kind: 'site-context',
        paths: ['sed.siteContext.text', 'sed.uploadedDocuments'],
        pattern:
          /(?:available\s+power\s+indication[^0-9\n]{0,40}?|indication\s+from\s+(?:the\s+)?utility[^0-9\n]{0,30}?|utility(?:['’]s)?\s+current\s+available\s+power[^0-9\n]{0,40}?)(\d[\d,]*(?:\.\d+)?)\s*MW/i,
        extract: 'numeric',
        min: 0.5,
        max: 20000,
        ref: 'text:grid.headroomIndicatedMw',
        label: 'Developer docs — utility-indicated available capacity',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── Interconnection queue duration (years) ──
  //
  // No slot candidate: the SED carries `interconnection.queueDurationP50Days`
  // (days) and `grid.estimatedQueueTime` (free text) — both need conversion
  // AND explicit site-load-service scope (regional/heuristic values are context),
  // which the builder performs as a DERIVED fill (see
  // `applyScoringDerivedFacts` in buildCanonicalFactStore.ts) so the
  // descriptor table stays a pure dot-path routing table.
  {
    factId: 'grid.queueYears',
    label: 'Interconnection queue (est.)',
    unit: 'years',
    valueKind: 'numeric',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.timeline', 'power.grid'],
        textMatch: /queue|interconnection/i,
        textExclude: CONTEXT_ONLY_QUEUE_TEXT,
        extract: 'numeric',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*(?:years?|yrs?)\b/i,
        ref: 'claim:grid.queueYears',
        label: 'Site-context queue claim',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── Interconnection voltage (kV) ──
  //
  // Tier-1 = the on-site KMZ substation's parsed kV (user ground truth).
  // The HIFLD `grid.voltage` STRING ("230 kV", "230/69 kV") is parsed by the
  // builder's derived fill when no numeric slot resolves.
  {
    factId: 'grid.voltageKv',
    label: 'Interconnection voltage',
    unit: 'kV',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.onSiteInfrastructure.data.substations.0.voltageKv',
        ref: 'sed:onSiteInfrastructure.substations.0.voltageKv',
        label: 'On-site KMZ substation',
      },
    ],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['power.grid'],
        textMatch: /\bkV\b|kilovolt/i,
        extract: 'numeric',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*kV/i,
        ref: 'claim:grid.voltageKv',
        label: 'Site-context voltage claim',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── Blended LCOE (economics anchor for the financial pillar) ──
  {
    factId: 'lcoe.blendedPerMwhUsd',
    label: 'Blended LCOE',
    unit: 'USD/MWh',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'provider',
        path: 'sed.lcoe.data.blendedLcoePerMwhUsd',
        ref: 'sed:lcoe.blendedLcoePerMwhUsd',
        label: 'LCOE section (computed)',
        // An 'unavailable' basis means the section could not compute a
        // grounded figure — any residual number is not a usable fact.
        absentWhen: { path: 'sed.lcoe.data.lcoeBasis', equals: ['unavailable'] },
      },
    ],
  },

  // ── Total CAPEX (financial assumptions) ──
  {
    factId: 'finance.capexTotalUsd',
    label: 'Total CAPEX',
    unit: 'USD',
    valueKind: 'numeric',
    candidates: [
      {
        tier: 1,
        kind: 'report-override',
        path: 'report.financialAssumptionsOverride.capexTotalUsd',
        ref: 'report:financialAssumptionsOverride.capexTotalUsd',
        label: 'Report override',
      },
      {
        tier: 1,
        kind: 'site-canonical',
        path: 'sed.financialAssumptions.capexTotalUsd',
        ref: 'sed:financialAssumptions.capexTotalUsd',
        label: 'Site financial assumptions',
      },
      {
        tier: 2,
        kind: 'report',
        path: 'report.financialAssumptionsAtGen.capexTotalUsd',
        ref: 'report:financialAssumptionsAtGen.capexTotalUsd',
        label: 'Financial assumptions at generation',
      },
    ],
  },

  // ── X-SED-6: developer-stated land facts (distinct from siteArea.acres) ──
  //
  // The live Redhead SED carries an 800-acre "buildable" claim and a
  // "~700–1,100 ac under control" band, both extracted under the single
  // `parcel.acres` claim category — so the generator/reviewer saw three
  // irreconcilable acreages for ONE slot (1170.1 boundary vs 800 vs the
  // band). Resolving them as their OWN labeled facts kills that
  // contradiction class at the root: the canonical block now lists
  // "Site area", "Buildable area (developer-stated)" and "Land under
  // control (developer-stated)" as distinct values, and the grounding
  // validator accepts a prose "800 acres (buildable)" as a match for the
  // buildable fact instead of flagging it against siteArea.acres.
  {
    factId: 'siteArea.buildableAcres',
    label: 'Buildable area (developer-stated)',
    unit: 'acres',
    valueKind: 'numeric',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['parcel.acres', 'parcel.boundaries', 'general'],
        textMatch: /buildable/i,
        extract: 'numeric',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*(?:acres?|ac\b)/i,
        ref: 'claim:siteArea.buildableAcres',
        label: 'Developer-stated buildable area',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },
  {
    factId: 'siteArea.underControlAcres',
    label: 'Land under control (developer-stated)',
    unit: 'acres',
    valueKind: 'numeric',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['parcel.acres', 'parcel.boundaries', 'general'],
        textMatch: /under\s+(?:site\s+)?control|site\s+control/i,
        textExclude: /buildable/i,
        extract: 'numeric-range',
        valuePattern: /(\d[\d,]*(?:\.\d+)?)\s*(?:acres?|ac\b)/i,
        ref: 'claim:siteArea.underControlAcres',
        label: 'Developer-stated land under control',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  /* ── X-SED-7: doc-tier grid-cost + fuel facts ───────────────────────────
   *
   * The live Redhead reviewer flagged "$22–32M" as an invented
   * interconnection cost when it is the SUM of two doc-stated ranges
   * ($4–7M 69 kV service + $18–25M 230 kV expansion) and read the generic
   * provider heuristic ($3–8M) as the site truth. Admitting the doc ranges
   * as their OWN labeled facts — and labeling the provider slot as the
   * heuristic it is — lets the reviewer prefer site-specific doc figures.
   * Ranges mirror the X-SED-6 under-control band (display band string +
   * numeric bounds into the USD allow-set). Both are developer statements ⇒
   * `labeled-unverifiable`.
   */
  {
    factId: 'grid.serviceUpgradeCost69kvUsd',
    label: '69 kV service upgrade cost (developer docs)',
    unit: 'USD million',
    valueKind: 'numeric',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['finance.cost'],
        textMatch: /69\s*kV[^.\n]{0,50}?(?:upgrade|substation|service)|(?:upgrade|service)[^.\n]{0,50}?69\s*kV/i,
        extract: 'numeric-range',
        rangePattern: /\$\s*\d[\d,]*(?:\.\d+)?\s*(?:[–—-]|to\s)\s*\d[\d,]*(?:\.\d+)?\s*(?:M\b|million)/i,
        valuePattern: /\$\s*(\d[\d,]*(?:\.\d+)?)\s*(?:M\b|million)/i,
        ref: 'claim:grid.serviceUpgradeCost69kvUsd',
        label: 'Developer docs — 69 kV service upgrade estimate',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },
  {
    factId: 'grid.expansionCost230kvUsd',
    label: '230 kV expansion cost (developer docs)',
    unit: 'USD million',
    valueKind: 'numeric',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['finance.cost'],
        textMatch: /230\s*kV[^.\n]{0,50}?expansion|expansion[^.\n]{0,50}?230\s*kV/i,
        extract: 'numeric-range',
        rangePattern: /\$\s*\d[\d,]*(?:\.\d+)?\s*(?:[–—-]|to\s)\s*\d[\d,]*(?:\.\d+)?\s*(?:M\b|million)/i,
        valuePattern: /\$\s*(\d[\d,]*(?:\.\d+)?)\s*(?:M\b|million)/i,
        ref: 'claim:grid.expansionCost230kvUsd',
        label: 'Developer docs — 230 kV expansion estimate',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },

  // ── X-SED-7: the provider interconnection-cost HEURISTIC, labeled ──
  //
  // `grid.estimatedInterconnectionCost` is a generic $-band the grid
  // provider stamps ("$3-8M"), not a site-specific study figure. It was
  // previously ungoverned, so the reviewer treated it as the site truth and
  // flagged the doc-derived $22–32M as an invention. Governing it WITH a
  // "provider heuristic" label makes its epistemic weight explicit next to
  // the doc-tier ranges above. Tier-2 ⇒ never `verified`.
  {
    factId: 'grid.estimatedInterconnectionCost',
    label: 'Interconnection cost (provider heuristic)',
    valueKind: 'categorical',
    candidates: [
      {
        tier: 2,
        kind: 'provider',
        path: 'sed.grid.data.estimatedInterconnectionCost',
        ref: 'sed:grid.estimatedInterconnectionCost',
        label: 'Grid provider heuristic (generic, not site-specific)',
      },
    ],
  },

  // ── X-SED-7: doc-established gas-pipeline adjacency ──
  //
  // The provider `fuelSupply.nearestPipelineKm` slot is honest-null on the
  // live site, but the developer's docs (Kinder Morgan call notes,
  // siteContext brief) establish the named pipeline ON the site ("Crossings:
  // Tennessee Gas Pipeline (east edge)"). Without a governed fact the
  // reviewer read every TGP mention as a fabrication. Value = the pipeline
  // NAME captured from the claim text ('text-pattern' — `claimedValue`
  // carries the OPERATOR); the relation lives in `basis`. The provider
  // `pipeline.distanceKm` stays honest-null: adjacency is a doc-stated
  // relation, never a surveyed distance.
  {
    factId: 'fuelSupply.pipelineAdjacency',
    label: 'Gas pipeline (developer docs)',
    valueKind: 'categorical',
    basis: 'adjacent/crossing per developer docs, not a surveyed distance',
    candidates: [],
    claimCandidates: [
      {
        tier: 2,
        kind: 'site-context',
        categories: ['doc-reference', 'general'],
        textMatch: /pipeline/i,
        extract: 'text-pattern',
        valuePattern: /\b((?:[A-Z][A-Za-z&.'’-]+[ \t]+){1,4}Pipeline)\b/,
        ref: 'claim:fuelSupply.pipelineAdjacency',
        label: 'Developer docs — pipeline adjacency',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
    textCandidates: [
      {
        tier: 3,
        kind: 'site-context',
        paths: ['sed.siteContext.text'],
        // "Crossings: Tennessee Gas Pipeline (east edge)" / "Pipeline:
        // Tennessee Gas Pipeline (TGP)" — a labeled pipeline designation in
        // the curated developer brief. NO /i flag: the NAME must stay
        // title-case-anchored (an idle lowercase "oil pipeline" aside never
        // resolves), so the label word carries explicit case classes instead.
        pattern: /(?:[Cc]rossings?|[Pp]ipeline)\s*:\s*((?:[A-Z][A-Za-z&.'’-]+[ \t]+){1,4}Pipeline)\b/,
        extract: 'text',
        ref: 'text:fuelSupply.pipelineAdjacency',
        label: 'Developer docs — pipeline adjacency',
        statusWhenWinner: 'labeled-unverifiable',
      },
    ],
  },
] as const;

/**
 * The fact ids the pillar scorer consumes (via `buildScoringFactSlice`).
 * A subset of `FACT_DESCRIPTORS` + the derived fills — kept as an explicit
 * list so the scoring slice never accidentally grows to the full prompt set
 * (power triplet, cooling axes, COD …) whose resolution needs the registry
 * manifest the scorer does not have.
 */
export const SCORING_FACT_IDS: readonly string[] = [
  'parcel.zoning',
  'wetlands.acres',
  'env.hasCriticalHabitat',
  'flood.zone',
  'flood.risk',
  'grid.headroomMw',
  'grid.queueYears',
  'grid.voltageKv',
  'substationDistKm',
  'lcoe.blendedPerMwhUsd',
  'finance.capexTotalUsd',
  'siteArea.acres',
  'siteArea.buildableAcres',
  'siteArea.underControlAcres',
] as const;

/**
 * Set of `factId`s whose ONLY tier-1 sources are US-only provider classes
 * (HIFLD grid, NREL, EIA, FEMA, …). On a degraded / non-US coverage SED these
 * facts' provider slots were SKIPPED, so any value reaching us came from a
 * non-authoritative path (AI research, intent guess) and must be marked
 * `'labeled-unverifiable'` rather than `'verified'` — the laundering guard.
 *
 * Intent-sourced facts (power.declaredMw, cooling.*, cod.targetDate) are NOT
 * in this set: a user-declared value is authoritative regardless of US
 * coverage.
 */
export const US_PROVIDER_DEPENDENT_FACT_IDS: ReadonlySet<string> = new Set([
  'electricUtility.name',
  'isoRegion',
  'substationDistKm',
  // X-SED-7 — the HIFLD-nearest substation NAME rides the same US-only
  // registry as its distance. The doc-tier siblings (interconnection
  // substation, retail/G&T names, indicated capacity, cost ranges, pipeline
  // adjacency) are NOT here — they are developer statements already stamped
  // labeled-unverifiable by their winning candidates.
  'grid.substationName',
  'siteArea.acres',
  'wetlands.acres',
  'fuelCostPerMwhUsd',
  // Scoring-critical facts whose only tier-1 sources are US-only providers
  // (Regrid / FEMA NFHL / USFWS / OASIS-ATC / HIFLD). NOT the claim-backed
  // developer facts (siteArea.buildable/underControl — already
  // labeled-unverifiable) and NOT the user-economics anchors (lcoe / capex).
  'parcel.zoning',
  'env.hasCriticalHabitat',
  'flood.zone',
  'flood.risk',
  'grid.headroomMw',
  'grid.queueYears',
  'grid.voltageKv',
  // Grounding FIX 1 distance facts backed by US-only registries (NRC reactor
  // registry, EIA pipeline registry). `ix.distanceKm` is NOT here — PeeringDB
  // is global. `technology.typicalModuleMWe` is class data, not provider data.
  'reactor.distanceKm',
  'pipeline.distanceKm',
]);
